import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { baseAPI } from '../config';
import userStore from '../store/userStore';
import { annotationAuthor } from '../utils/annotationAuthor';
import { SIDE_LABELS, effectiveRecipients } from '@claimup/shared/helpers/annotation-recipients';

// Las tres vistas que sirve `POST /annotations/alltasks` (campo `view`).
const TABS = [
  { id: 'pending', label: 'Pendientes', empty: 'No tienes tareas pendientes.' },
  {
    id: 'waiting',
    label: 'Esperando a la otra parte',
    empty: 'No hay tareas tuyas pendientes de que las complete la otra parte.',
    help: 'Tareas que tú o tu equipo habéis dejado y que la otra parte aún no ha completado.',
  },
  {
    id: 'completed',
    label: 'Completadas',
    empty: 'Todavía no hay tareas completadas.',
    help: 'Las últimas tareas que iban para ti y ya están completadas.',
  },
];

const formatDate = value => (value ? new Date(value).toLocaleDateString('es-ES') : '');

const listLabels = sides => sides.map(s => SIDE_LABELS[s] || s).join(' y ');

const ExpedientLink = ({ expedientId }) => (
  <a href={`/view/${expedientId}`} className='shrink-0 text-slate-400 hover:text-slate-500 ml-2'>
    <span className='sr-only'>Ver expediente</span>
    <svg className='w-4 h-4' fill='none' stroke='currentColor' viewBox='0 0 24 24'>
      <path
        strokeLinecap='round'
        strokeLinejoin='round'
        strokeWidth='2'
        d='M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122'
      />
    </svg>
  </a>
);

function TaskRow({ task, view, viewerRole, onComplete }) {
  // El backend trae el deudor en `deudores` (antes se buscaba en
  // `expedient_id.deudores_id`, que es un id, y nunca salía).
  const debtor = task.deudores?.[0];
  const done = view === 'completed';

  return (
    <div className={`px-2 py-3 rounded-lg border ${done ? 'bg-slate-50 border-slate-100' : 'border-slate-200 hover:border-slate-300'}`}>
      <div className='flex items-start justify-between'>
        <div className='grow flex items-start space-x-3'>
          {view === 'pending' && (
            <input
              type='checkbox'
              aria-label='Marcar como completada'
              className='h-4 w-4 rounded-lg border-slate-300 text-blue-600 focus:ring-blue-500 mt-1'
              onChange={e => e.target.checked && onComplete(task._id)}
            />
          )}
          {done && <div className='h-4 w-4 mt-1 text-emerald-600'>✓</div>}
          <div>
            <div className={`text-sm font-medium text-slate-800 break-all ${done ? 'line-through opacity-60' : ''}`}>
              {task.anotacion}
            </div>
            {debtor && (
              <div className='mt-0.5 text-xs text-slate-500'>
                {debtor.debtorname}
                {debtor.debtornif ? ` · ${debtor.debtornif}` : ''}
              </div>
            )}
            <div className='mt-0.5 text-xs text-slate-400'>
              {formatDate(task.fecha)} · {annotationAuthor(task, viewerRole)}
              {view === 'waiting' && <> · Para {task.para_usuario_name || listLabels(effectiveRecipients(task))}</>}
              {done && task.completed_at && <> · Completada el {formatDate(task.completed_at)}</>}
            </div>
          </div>
        </div>
        <ExpedientLink expedientId={task.expedient_id} />
      </div>
    </div>
  );
}

function TasksList() {
  const [view, setView] = useState('pending');
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = userStore();

  useEffect(() => {
    if (!user) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    const fetchTasks = async () => {
      setLoading(true);
      try {
        const response = await axios.post(
          `${baseAPI}/annotations/alltasks`,
          { view },
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        // A quién le sale cada tarea y en qué pestaña lo decide el backend.
        if (!cancelled) setTasks(response.data);
      } catch (error) {
        console.error('Failed to fetch tasks:', error);
        if (!cancelled) toast.error('Error al cargar las tareas');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTasks();
    return () => {
      cancelled = true;
    };
  }, [user, view]);

  const onComplete = async taskId => {
    try {
      await axios.put(
        `${baseAPI}/annotations/completedtask/${taskId}`,
        {},
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      toast.success('Tarea completada!');
      // Sale de Pendientes; la encontrará en Completadas.
      setTasks(prev => prev.filter(task => task._id !== taskId));
    } catch (error) {
      console.error('Failed to complete task:', error);
      toast.error('Error al completar la tarea');
    }
  };

  const tab = TABS.find(t => t.id === view);
  // Pendientes: la más antigua primero (es lo que lleva más tiempo esperando).
  // Las otras dos ya vienen ordenadas del backend, la más reciente primero.
  const rows =
    view === 'pending' ? [...tasks].sort((a, b) => new Date(a.fecha) - new Date(b.fecha)) : tasks;

  return (
    <div className='col-span-full xl:col-span-12'>
      <div className='bg-white shadow-lg rounded-lg border border-slate-200'>
        <header className='px-5 py-4 border-b border-slate-100'>
          <h2 className='font-semibold text-slate-800'>Tareas</h2>
        </header>

        <div className='border-b border-slate-200'>
          <nav className='flex flex-wrap gap-x-6 px-5' role='tablist'>
            {TABS.map(t => (
              <button
                key={t.id}
                type='button'
                role='tab'
                aria-selected={view === t.id}
                onClick={() => setView(t.id)}
                className={`py-3 text-sm font-medium border-b-2 transition-colors outline-none focus:outline-none ${
                  view === t.id ? 'border-blue-600 text-blue-600' : 'border-transparent text-slate-500 hover:text-slate-700'
                }`}
              >
                {t.label}
                {view === t.id && !loading && <span className='ml-1 text-slate-400'>({tasks.length})</span>}
              </button>
            ))}
          </nav>
        </div>

        <div className='p-3'>
          {tab.help && <p className='px-2 pb-3 text-xs text-slate-500'>{tab.help}</p>}

          {loading ? (
            <div className='text-center py-10'>
              <span className='text-sm font-medium text-slate-500'>Cargando...</span>
            </div>
          ) : rows.length === 0 ? (
            <div className='text-center py-10'>
              <h3 className='text-base font-medium text-slate-800'>No hay tareas</h3>
              <p className='text-sm text-slate-500 mt-1'>{tab.empty}</p>
            </div>
          ) : (
            <div className='space-y-2'>
              {rows.map(task => (
                <TaskRow key={task._id} task={task} view={view} viewerRole={user?.role} onComplete={onComplete} />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default TasksList;
